import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Invoice, InvoicePayload } from '../models';
import { ServiceRespose } from '../../shared';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  http = inject(HttpClient);

  private readonly baseApiUrl = `${environment.invoiceServiceApiBaseUrl}/api/invoice`;

  // Property to store last invoice number
  private lastInvoiceNumber = 1;

  constructor() {
    const storedNumber = localStorage.getItem('lastInvoiceNumber');
    if (storedNumber) {
      this.lastInvoiceNumber = +storedNumber;
    }
  }

  /**
   * Create invoice 
   */
  createInvoice(invoice: InvoicePayload):Observable<Invoice>{
    return this.http
        .post<ServiceRespose<Invoice>>(`${this.baseApiUrl}`, invoice)
        .pipe(
          tap((res) => {
            if(res.isSuccess){
              const invoiceNo = res.data.invoiceMetadata.invoiceNumber;
              this.lastInvoiceNumber = +invoiceNo;

              localStorage.setItem('lastInvoiceNumber', this.lastInvoiceNumber.toString());
            }
          }),
          map((res) => res.data),
          catchError((err) => throwError(() => new Error(err.message)))
        )
  }

  /**
   * Get invoice by Invoice Id
   */
  getInvoiceById(id: string) : Observable<Invoice>{
    return this.http
        .get<ServiceRespose<Invoice>>(`${this.baseApiUrl}/${id}`)
        .pipe(
          map((res) => res.data),
          catchError((err) => throwError(() => new Error(err.message)))
        )
  }

  /**
   * Get all invoices for current user
   */
  getInvoicesByUserId(userId:string):Observable<Invoice[]>{
    return this.http
        .get<ServiceRespose<Invoice[]>>(`${this.baseApiUrl}/all/${userId}`)
        .pipe(
          map((res) => res.data),
          catchError((err) => throwError(() => new Error(err.message)))
        )
  }

  /**
   * Get all invoices for current user
   */
  getInvoicePdfById(id: string): Observable<HttpResponse<Blob>>{
    return this.http
        .get(`${this.baseApiUrl}/pdf/${id}`,{
          responseType: 'blob',
          observe: 'response'
        }).pipe(
          catchError((err) => throwError(() => new Error(err.message)))
        )
  }

  /**
   * Download invoice as PDF by id
   */
  downloadPdf(id: string): void {
    this.http.get(`${this.baseApiUrl}/${id}/pdf`, { responseType: 'blob' })
      .subscribe((blob: Blob) => {
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = `invoice-${id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      });
  }

  /**
   * Get last invoice number
   */
  getLastInvoiceNumber(): number {
    return this.lastInvoiceNumber;
  }
}
